import { Navigate, Outlet, useParams } from 'react-router';
import { Suspense } from 'react';

import { HomeRoutes, StudioRoutes } from '../../../../RouteConstants';
import { IRoutes } from '@components/3.0/Layouts/TabbedLayout/utils/constants/routes.interface';
import RouteErrorPage from '@components/ErrorBoundary/RouteErrorPage';
import { LoadingIndicator } from '@components/LoadingIndicatorMaterial';
import { ChunkErrorBoundary } from '@components/ErrorBoundary';
import { notFoundRoute } from './404.routes';

// redirect for old route, in case someone has a domain id bookmarked
const EditDomainRedirect = () => {
  const params = useParams();
  return <Navigate to={`${StudioRoutes.editDomain.base}/${params.domainId}`} />;
};

export const editDomainRoutes: IRoutes[] = [
  {
    routeItem: {
      path: `${StudioRoutes.model.base}`,
      children: [
        {
          path: '',
          element: <Outlet />,
          errorElement: <RouteErrorPage />,
          children: [
            {
              path: '',
              hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
              async lazy() {
                const Domains = (await import('Modules/Studio/Domains/Domains')).default;
                const ToggleComponentBasedOnPermission = (
                  await import('RBAC/ToggleComponentBasedOnPermission')
                ).default;

                return {
                  element: (
                    <ToggleComponentBasedOnPermission
                      IsAllowed={Domains}
                      IsNotAllowed={() => <Navigate to={`/${HomeRoutes.notAuthorized}`} />}
                      permissionRef={StudioRoutes.model.permission}
                    />
                  ),
                  errorElement: <RouteErrorPage />,
                };
              },
            },
            {
              path: `${StudioRoutes.editDomain.base}${StudioRoutes.editDomain.basePathVariables}/*`,
              hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
              children: [
                {
                  path: '',
                  hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
                  async lazy() {
                    const DomainDetails = (
                      await import('Modules/Studio/DomainEditor/DomainDetails')
                    ).default;

                    return {
                      element: (
                        <ChunkErrorBoundary>
                          <Suspense fallback={<LoadingIndicator isLoading />}>
                            <DomainDetails />
                          </Suspense>
                        </ChunkErrorBoundary>
                      ),
                      errorElement: <RouteErrorPage />,
                    };
                  },
                },
                {
                  path: `${StudioRoutes.editDomainMapping.base}${StudioRoutes.editDomainMapping.basePathVariables}`,
                  id: 'mappingViewByConceptUri',
                  hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
                  async lazy() {
                    const EditDomainMappingView = (
                      await import('Modules/Studio/Mappings/EditDomainMappingView')
                    ).default;
                    const { fetchMappingListByConceptUri } = await import(
                      'Modules/Studio/Mappings/routing/fetchMappingListByConceptUri.loader'
                    );

                    return {
                      element: (
                        <ChunkErrorBoundary>
                          <Suspense fallback={<LoadingIndicator isLoading />}>
                            <EditDomainMappingView />
                          </Suspense>
                        </ChunkErrorBoundary>
                      ),
                      loader: fetchMappingListByConceptUri,
                      errorElement: <RouteErrorPage />,
                    };
                  },
                },

                // match wildcard route for any other path under edit-domain & show 404 page
                notFoundRoute.routeItem,
              ],
              async lazy() {
                return {
                  element: (
                    <ChunkErrorBoundary>
                      <Suspense fallback={<LoadingIndicator isLoading />}>
                        <Outlet />
                      </Suspense>
                    </ChunkErrorBoundary>
                  ),
                  errorElement: <RouteErrorPage />,
                };
              },
            },
            {
              // legacy route for edit-domain, redirect to new route
              path: `edit-domain${StudioRoutes.editDomain.basePathVariables}`,
              element: <EditDomainRedirect />,
              errorElement: <RouteErrorPage />,
            },
          ],
        },
        notFoundRoute.routeItem,
      ],
    },
    permissionRef: StudioRoutes.model.permission,
    featureFlag: true,
  },
];
